import React from "react";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { analyseTheme } from "../../analyse/analyseTheme";
import { panelCardStyles } from "./sharedStyles";

const styles = (theme: Theme) =>
  createStyles({
    ...panelCardStyles(theme),
    "@global": {
      "@keyframes homepageSkeletonPulse": {
        "0%": { opacity: 1 },
        "50%": { opacity: 0.45 },
        "100%": { opacity: 1 },
      },
    },
    bone: {
      backgroundColor: analyseTheme.borderLight,
      borderRadius: analyseTheme.radiusXs,
      animation: "homepageSkeletonPulse 1.5s ease-in-out infinite",
    },
    resumeCardSquare: {
      width: 280,
      minWidth: 280,
      aspectRatio: "1",
      flexShrink: 0,
      borderLeft: `4px solid ${analyseTheme.borderLight}`,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: 0,
        aspectRatio: "auto",
        minHeight: 220,
      },
    },
    activeBatchesCard: {
      flex: 1,
      minWidth: 0,
    },
    activeBatchList: {
      display: "flex",
      flexDirection: "column" as const,
      gap: `${theme.spacing.unit / 2}px`,
      flex: 1,
    },
    activeBatchRow: {
      display: "flex",
      flexDirection: "row" as const,
      alignItems: "center",
      gap: `${theme.spacing.unit * 2}px`,
      padding: "6px 8px",
      margin: "0 -8px",
      borderBottom: `1px solid ${analyseTheme.borderLight}`,
      "&:last-child": {
        borderBottom: "none",
      },
    },
    activeBatchNameBone: {
      flex: 1,
      minWidth: 0,
      height: 16,
    },
    activeBatchProgressBone: {
      width: 140,
      flexShrink: 0,
    },
    recentProjectsCard: {
      flex: 1,
      minWidth: 0,
    },
    listRow: {
      display: "flex",
      alignItems: "center",
      gap: `${theme.spacing.unit * 2}px`,
      padding: "8px 0",
      borderBottom: `1px solid ${analyseTheme.borderLight}`,
      "&:last-child": {
        borderBottom: "none",
      },
    },
    assignedRow: {
      backgroundColor: analyseTheme.surface,
      border: `1px solid ${analyseTheme.border}`,
      borderRadius: analyseTheme.radiusSm,
      padding: `${theme.spacing.unit * 2}px`,
      marginBottom: theme.spacing.unit,
      display: "flex",
      alignItems: "center",
      gap: `${theme.spacing.unit * 2}px`,
    },
    assignedBatchNameRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
  });

interface BoneProps {
  classes: Record<string, string>;
  width: number | string;
  height: number;
  style?: React.CSSProperties;
}

const Bone = ({ classes, width, height, style }: BoneProps) => (
  <div
    className={classes.bone}
    style={{ width, height, flexShrink: 0, ...style }}
  />
);

export const ResumeBatchWidgetSkeleton = withStyles(styles)(
  ({ classes }: { classes: Record<string, string> }) => (
    <Paper
      elevation={0}
      className={`${classes.panelCard} ${classes.resumeCardSquare}`}
    >
      <Typography className={classes.sectionTitle} variant="h6">
        Reprendre le dernier lot
      </Typography>
      <Bone classes={classes} width="75%" height={20} />
      <Bone classes={classes} width="55%" height={14} style={{ marginTop: 10 }} />
      <Bone classes={classes} width="100%" height={8} style={{ marginTop: 16 }} />
      <div style={{ flexGrow: 1 }} />
      <Bone classes={classes} width={120} height={36} style={{ marginTop: 16 }} />
    </Paper>
  ),
);

export const ActiveBatchesWidgetSkeleton = withStyles(styles)(
  ({ classes }: { classes: Record<string, string> }) => (
    <Paper
      elevation={0}
      className={`${classes.panelCard} ${classes.activeBatchesCard}`}
    >
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionHeaderTitle} variant="h6">
          Mes lots en cours
        </Typography>        
      </div>
      <div className={classes.activeBatchList}>
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className={classes.activeBatchRow}>
            <div className={classes.activeBatchNameBone}>
              <Bone classes={classes} width="70%" height={16} />
            </div>
            <div className={classes.activeBatchProgressBone}>
              <Bone classes={classes} width="100%" height={8} />
            </div>
          </div>
        ))}
      </div>
    </Paper>
  ),
);

export const RecentProjectsWidgetSkeleton = withStyles(styles)(
  ({ classes }: { classes: Record<string, string> }) => (
    <Paper
      elevation={0}
      className={`${classes.panelCard} ${classes.recentProjectsCard}`}
    >
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionHeaderTitle} variant="h6">
          Les derniers projets
        </Typography>        
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={classes.listRow}>
          <Bone classes={classes} width="auto" height={16} style={{ flex: 1, minWidth: 0 }} />
          <Bone classes={classes} width={80} height={14} />
          <Bone classes={classes} width={90} height={14} />
          <Bone classes={classes} width={24} height={14} />
        </div>
      ))}
    </Paper>
  ),
);

export const AssignedBatchesWidgetSkeleton = withStyles(styles)(
  ({ classes }: { classes: Record<string, string> }) => (
    <>
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionHeaderTitle} variant="h6">
          Mes lots assignés
        </Typography>        
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className={classes.assignedRow}>
          <div style={{ flex: 1 }}>
            <div className={classes.assignedBatchNameRow}>
              <Bone classes={classes} width={20} height={20} />
              <Bone classes={classes} width="55%" height={18} style={{ flex: 1, minWidth: 0 }} />
            </div>
            <Bone classes={classes} width="40%" height={14} style={{ marginTop: 8 }} />
            <Bone classes={classes} width={160} height={22} style={{ marginTop: 10 }} />
          </div>
          <Bone classes={classes} width={180} height={8} />
        </div>
      ))}
    </>
  ),
);
